import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { ProgressSpinner } from 'primereact/progressspinner';
import { PriorityDTO } from '../types/agenda.types';
import { getPriorities, assignPriority } from '../services/AgendaService';
import { parseApiError } from '../types/error.types';
import { FieldError } from '../../../components/novarium/NvTableParts';
import './AssignPriorityModal.scss';

interface AssignPriorityModalProps {
  visible: boolean;
  appointmentId: number | null;
  patientName?: string;
  currentPriorityId?: number | null;
  onHide: () => void;
  onAssigned: () => void;
  onError?: (message: string) => void;
}

export default function AssignPriorityModal({
  visible,
  appointmentId,
  patientName,
  currentPriorityId,
  onHide,
  onAssigned,
  onError,
}: AssignPriorityModalProps) {
  const [priorities, setPriorities] = useState<PriorityDTO[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    setSelectedId(currentPriorityId ?? null);
    setError(undefined);
    getPriorities()
      .then(setPriorities)
      .catch(() => setPriorities([]))
      .finally(() => setLoading(false));
  }, [visible, currentPriorityId]);

  // "Asignar" siempre habilitado: sin prioridad elegida, se marca el grupo y no se envía
  const handleSave = async () => {
    if (!appointmentId) return;
    if (!selectedId) {
      setError('Elegí una prioridad');
      return;
    }
    setSaving(true);
    try {
      await assignPriority(appointmentId, selectedId);
      onAssigned();
    } catch (err) {
      onError?.(parseApiError(err, 'No se pudo asignar la prioridad. Intentá de nuevo.'));
    } finally {
      setSaving(false);
    }
  };

  const header = patientName ? `Asignar prioridad · ${patientName}` : 'Asignar prioridad';

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={header}
      modal
      closable
      draggable={false}
      className="nv-scope nv-dialog nv-dialog-s"
      style={{ width: '400px', maxWidth: '95vw' }}
      footer={(
        <>
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onHide} disabled={saving} />
          <Button type="button" className="buttonMain" label="Asignar" onClick={handleSave} loading={saving} />
        </>
      )}
    >
      {loading ? (
        <div className="nv-loading" role="status">
          <ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Cargando…
        </div>
      ) : (
        <div
          className="assign-priority__options"
          role="radiogroup"
          aria-label="Prioridad"
          aria-describedby={error ? 'assign-priority-error' : undefined}
        >
          {/* TODO(design-system): novarium.scss no estiliza RadioButton; hoy lo pinta index.css (relleno gris, sin borde) y
              el tema saga (azul #2196F3 al elegir). El DS pide marcado en --primary y vacío blanco con borde. */}
          {priorities.map((p) => (
            <div key={p.id} className="nv-check-row">
              <RadioButton
                inputId={`assign-priority-${p.id}`}
                name="assign-priority"
                value={p.id}
                checked={selectedId === p.id}
                onChange={() => {
                  setSelectedId(p.id);
                  setError(undefined);
                }}
              />
              <label htmlFor={`assign-priority-${p.id}`}>{p.description}</label>
            </div>
          ))}
          <FieldError id="assign-priority-error" message={error} />
        </div>
      )}
    </Dialog>
  );
}
